import serviceMapping from '../serviceMapping.json';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class CleaningServiceCatalog {
  
  /**
   * Fetches all main services from the service catalog API
   * @param {string} tenantId - The tenant identifier
   * @param {string} cityCode - The city code for the service area
   * @returns {Promise<Array>} Array of main service objects with catalogID, serviceId, type, name, icon, description, priceModel, unit, rate
   */
  async getMainServices(tenantId, cityCode) {
    try {
      if (!tenantId || !cityCode) {
        throw new Error('tenantId and cityCode are required parameters');
      }

      const response = await fetch(
        `${API_BASE}/api/service-catalog?type=main&tenantId=${tenantId}&cityCode=${cityCode}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const mainServices = await response.json();
      
      // Log service names for debugging
      console.log('API returned main services:', mainServices.map(s => ({
        id: s.id,
        name: s.name,
        type: s.type
      })));
      
      // Transform the response to match the expected format
      return mainServices.map(service => ({
        catalogID: service.id,
        serviceId: this._getServiceId(service.name),
        type: service.type,
        name: service.name,
        icon: service.icon,
        description: service.description,
        priceModel: service.priceModel,
        unit: service.unit,
        rate: service.rate
      }));

    } catch (error) {
      console.error('Error fetching main services:', error);
      throw new Error(`Failed to fetch main services: ${error.message}`);
    }
  }

  /**
   * Fetches extra services that are compatible with the given main service
   * @param {string} mainServiceName - The name of the main service (e.g., "General Cleaning")
   * @param {string} tenantId - The tenant identifier
   * @param {string} cityCode - The city code for the service area
   * @returns {Promise<Array>} Array of compatible extra service objects
   */
  async getCompatibleExtras(mainServiceName, tenantId, cityCode) {
    try {
      if (!mainServiceName || !tenantId || !cityCode) {
        throw new Error('mainServiceName, tenantId and cityCode are required parameters');
      }

      // Find the main service by name in the new structure
      const mainServiceEntry = Object.entries(serviceMapping.mainServices).find(
        ([, service]) => service.name === mainServiceName
      );

      if (!mainServiceEntry) {
        console.warn(`Main service "${mainServiceName}" not found in service mapping. This service may not have compatible extras defined, or it may be miscategorized in the database.`);
        
        // Check if this service is actually in the extraServices mapping
        const extraServiceEntry = Object.entries(serviceMapping.extraServices).find(
          ([, service]) => service.name === mainServiceName
        );
        
        if (extraServiceEntry) {
          console.warn(`"${mainServiceName}" was found in extraServices but returned as main service by API. This suggests a data categorization issue.`);
        }
        
        return []; // Return empty array instead of throwing error
      }

      const [, mainServiceConfig] = mainServiceEntry;
      const compatibleExtraUUIDs = mainServiceConfig.compatibleExtras;
      
      if (!compatibleExtraUUIDs || compatibleExtraUUIDs.length === 0) {
        return []; // No compatible extras for this main service
      }

      // Fetch all extra services from the API
      const response = await fetch(
        `${API_BASE}/api/service-catalog?type=extra&tenantId=${tenantId}&cityCode=${cityCode}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const allExtraServices = await response.json();
      
      // Filter only the compatible extras based on UUIDs from service mapping
      const compatibleExtras = allExtraServices.filter(service => {
        return compatibleExtraUUIDs.includes(service.id);
      });

      // Transform the response to match the expected format
      return compatibleExtras.map(service => ({
        catalogID: service.id,
        serviceId: this._getServiceId(service.name),
        type: service.type,
        name: service.name,
        icon: service.icon,
        description: service.description,
        priceModel: service.priceModel,
        unit: service.unit,
        rate: service.rate
      }));

    } catch (error) {
      console.error('Error fetching compatible extras:', error);
      throw new Error(`Failed to fetch compatible extras: ${error.message}`);
    }
  }

  /**
   * Helper method to convert service name to service ID format
   * @private
   * @param {string} serviceName - The display name of the service
   * @returns {string} The service ID in kebab-case format
   */
  _getServiceId(serviceName) {
    return serviceName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  }

  /**
   * Gets the service mapping configuration for a main service
   * @param {string} mainServiceName - The name of the main service
   * @returns {Object|null} The service configuration object or null if not found
   */
  getServiceMapping(mainServiceName) {
    const mainServiceEntry = Object.entries(serviceMapping.mainServices).find(
      ([, service]) => service.name === mainServiceName
    );
    return mainServiceEntry ? mainServiceEntry[1] : null;
  }

  /**
   * Gets all available main service names from the service mapping
   * @returns {Array<string>} Array of main service names
   */
  getAvailableMainServiceNames() {
    return Object.values(serviceMapping.mainServices).map(service => service.name);
  }

  /**
   * Gets the service mapping configuration by UUID
   * @param {string} mainServiceUUID - The UUID of the main service
   * @returns {Object|null} The service configuration object or null if not found
   */
  getServiceMappingByUUID(mainServiceUUID) {
    return serviceMapping.mainServices[mainServiceUUID] || null;
  }

  /**
   * Gets all available main services with their UUIDs
   * @returns {Array<Object>} Array of main service objects with UUID, name, and serviceId
   */
  getAvailableMainServices() {
    return Object.entries(serviceMapping.mainServices).map(([uuid, service]) => ({
      uuid,
      name: service.name,
      serviceId: service.serviceId
    }));
  }
}

// Export a singleton instance
const cleaningServiceCatalogInstance = new CleaningServiceCatalog();
export default cleaningServiceCatalogInstance;